const workspaceBlockingRuleFragments = [
    "conflicts with current network evidence",
    "possible natural-person identity",
    "stale unverified exact-ip evidence",
    "provider network owner does not identify the downstream customer",
    "ai arbitration selected a different end company",
    "no independently supported end-company link",
    "legal-entity review required",
    "network allocation was not validated"
];
const technicalOnlyStandaloneEvidenceFamilies = new Set([
    // Prefix2Org links a routed prefix to an ASN/operator. The XFLOWLY v12
    // holdout showed that this source alone does not identify the actual
    // visiting end-customer company (0/68 correct). It remains valuable
    // technical evidence, but needs a separate company-link signal.
    "prefix2org",
    "openai_company_arbitration"
]);
/**
 * Sources restored from the first Moonshot version (commit 74b0c42). Each keeps
 * its own family so a recognition gain can be attributed to exactly one source.
 */
export const V1_RESTORED_EVIDENCE_FAMILIES = ["registry_description", "learned_mapping", "internetdb_hostname"];
/**
 * Marks the candidate the first version's publish rule (score >= .74, or AI
 * company decision >= .6 with score >= .52, ...) put into the workspace although
 * the evidence-quality policy below would not. Workspace display only: it never
 * counts as an independent family and never enables external publication.
 */
export const V1_PUBLISH_RULE_FAMILY = "v1_publish_rule";
/**
 * Evidence source of a learned mapping a user created by correcting a visit's
 * company. A person confirmed the company, so an unknown legal form or a
 * private/residential line does not keep it out of the workspace.
 */
export const MANUAL_CORRECTION_SOURCE = "manual correction";
/** Account-signal evidence family of a visit whose company a person set or confirmed. Workspace only. */
export const MANUAL_CORRECTION_FAMILY = "manual_correction";
export function hasManualCompanyConfirmation(candidate) {
    return candidate.evidence.some(item => item.family === "learned_mapping" && item.direction === "positive"
        && item.weight > 0 && Boolean(item.source?.startsWith(MANUAL_CORRECTION_SOURCE)));
}
export function hasV1PublishRule(candidate) {
    return candidate.evidence.some(item => item.family === V1_PUBLISH_RULE_FAMILY && item.direction === "positive" && item.weight > 0);
}
const directCompanyEvidenceFamilies = new Set([
    "tenant_exact_claim", "tenant_observation", "verified_claim",
    "edge_fcrdns", "edge_fcrdns_discovery", "edge_ip_company",
    "edge_prefix_consensus", "domain_asset", "reverse_dns", "internetdb_forward_dns", "edge_dns_footprint", "network_probe_forward_dns", "rdap_registrant_allocation",
    ...V1_RESTORED_EVIDENCE_FAMILIES, V1_PUBLISH_RULE_FAMILY
]);
export function hasDirectCompanyEvidence(candidate) {
    return candidate.evidence.some(item => item.direction === "positive"
        && item.weight > 0 && directCompanyEvidenceFamilies.has(item.family));
}
/** Multiple processing steps over the same underlying source are one family. */
export function independentCompanyEvidenceFamilies(candidate) {
    const related = {
        edge_fcrdns: "dns", edge_fcrdns_discovery: "dns", reverse_dns: "dns", dns: "dns",
        internetdb_forward_dns: "dns", edge_dns_footprint: "dns", network_probe_forward_dns: "dns",
        edge_ip_company: "ip_company", edge_prefix_consensus: "ip_company", licensed_ip_company: "ip_company",
        rir_rdap: "allocation", rdap: "allocation", delegation: "allocation", rdap_registrant_allocation: "allocation",
        registry_description: "allocation",
        tenant_exact_claim: "tenant_history", tenant_observation: "tenant_history", verified_claim: "tenant_history",
        learned_mapping: "tenant_history", internetdb_hostname: "dns",
        bgp: "routing", rpki: "routing", prefix2org: "routing", as2org: "routing"
    };
    return [...new Set(candidate.evidence.filter(item => item.direction === "positive" && item.weight > 0
            && item.family !== "openai_company_arbitration" && item.family !== V1_PUBLISH_RULE_FAMILY)
            .map(item => related[item.family] ?? item.family))];
}
/**
 * Determines whether a resolver candidate may name a company in the normal
 * activity workspace. This is deliberately an evidence-quality policy rather
 * than a confidence threshold: low-scoring but genuine company-link evidence
 * remains visible, while proven operator-only or stale clues stay in the
 * Request Explorer without being presented as the visitor's company.
 */
export function workspaceCandidateAssociationDecision(candidate) {
    const positiveEvidenceFamilies = [...new Set(candidate.evidence
            .filter((item) => item.direction === "positive" && item.weight > 0)
            .map((item) => item.family))];
    if (candidate.companyLink?.kind === "operator_only") {
        return { eligible: false, positiveEvidenceFamilies, reason: "Only the network operator is evidenced, not a visiting company." };
    }
    // Deliberate, labelled exception for validating the first version's rule.
    if (hasV1PublishRule(candidate))
        return { eligible: true, positiveEvidenceFamilies };
    if (candidate.companyLink && (candidate.companyLink.kind === "unresolved" || !candidate.companyLink.allocationMatched)
        && !candidate.evidence.some(item => item.direction === "positive" && item.weight > 0
            && directCompanyEvidenceFamilies.has(item.family) && !["edge_ip_company", "edge_prefix_consensus"].includes(item.family))) {
        return { eligible: false, positiveEvidenceFamilies, reason: "The company allocation has not been validated against this request." };
    }
    const blockingRule = candidate.hardRules.find((rule) => workspaceBlockingRuleFragments.some((fragment) => rule.toLowerCase().includes(fragment)));
    if (blockingRule) {
        return {
            eligible: false,
            positiveEvidenceFamilies,
            reason: `Workspace association blocked by unsafe or outdated evidence: ${blockingRule}`
        };
    }
    if (!positiveEvidenceFamilies.length) {
        return {
            eligible: false,
            positiveEvidenceFamilies,
            reason: "No positive evidence supports a company association."
        };
    }
    if (positiveEvidenceFamilies.every((family) => technicalOnlyStandaloneEvidenceFamilies.has(family))) {
        return {
            eligible: false,
            positiveEvidenceFamilies,
            reason: "Network-operator evidence alone does not identify the visiting end-customer company."
        };
    }
    return { eligible: true, positiveEvidenceFamilies };
}
//# sourceMappingURL=workspace-association.js.map